/* Keep Judge.me's app markup/settings; prepare its native renderer near the section. */
(() => {
  if (customElements.get('dio-review-lazy')) return;
  const manifests = new Map();
  const style = url => {
    if ([...document.querySelectorAll('link[rel="stylesheet"]')].some(link => link.href === url)) return;
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = url; document.head.append(link);
  };
  const manifest = base => {
    if (!manifests.has(base)) manifests.set(base, fetch(new URL('manifest.json', base)).then(response => {
      if (!response.ok) throw new Error('Review manifest unavailable');
      return response.json();
    }).catch(error => { manifests.delete(base); throw error; }));
    return manifests.get(base);
  };
  const styles = (data, key, base, seen = new Set()) => {
    if (seen.has(key)) return; seen.add(key);
    const entry = data[key]; if (!entry) return;
    (entry.css || []).forEach(file => style(new URL(file, base).href));
    (entry.imports || []).forEach(file => styles(data, file, base, seen));
  };
  class ReviewLazy extends HTMLElement {
    connectedCallback() {
      this.generation = (this.generation || 0) + 1;
      if (this.querySelector('[data-dio-review-ready]')) this.start();
      else {
        this.parser = new MutationObserver(() => {
          if (this.querySelector('[data-dio-review-ready]')) { this.parser.disconnect(); this.start(); }
        });
        this.parser.observe(this, {childList:true, subtree:true});
      }
    }
    start() {
      if (this.started || !this.isConnected) return;
      this.started = true;
      this.root = this.querySelector('[data-dio-review-entry-point]');
      if (!this.root) return;
      this.intent = event => {
        if (event.target.closest?.('.jdgm-preview-badge,.jdgm-all-reviews-text,a[href*="#judgeme"]')) this.load();
      };
      this.hash = () => { if (/judgeme|review/i.test(location.hash) || /judgeme_review_uuid|judgeme_token/.test(location.search)) this.load(); };
      document.addEventListener('click', this.intent, true);
      window.addEventListener('hashchange', this.hash);
      if (window.Shopify?.designMode || !window.IntersectionObserver) this.load();
      else {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.load();
        }, {rootMargin:'1400px 0px'});
        this.observer.observe(this);
        this.hash();
      }
    }
    async load() {
      if (this.pending || this.manager || !this.root || !this.isConnected) return;
      const generation = this.generation;
      this.pending = true;
      try {
        const base = window.jdgm?.CDN_BASE_URL;
        if (!base) throw new Error('Review configuration is not ready');
        const data = await manifest(base);
        const key = this.root.dataset.entryKey;
        const entry = data[key];
        const managerKey = (entry?.imports || []).find(name => /ReviewWidgetManager/.test(name));
        const managerEntry = managerKey && data[managerKey];
        if (!entry || !managerEntry?.file) { await this.nativeFallback(base, data, key); return; }
        styles(data, key, base);
        const module = await import(new URL(managerEntry.file, base).href);
        if (!this.isConnected || this.generation !== generation) return;
        if (typeof module.ReviewWidgetManager !== 'function') { await this.nativeFallback(base, data, key); return; }
        this.root.dataset.entryPoint = this.root.dataset.dioReviewEntryPoint;
        const manager = new module.ReviewWidgetManager(this.root);
        await manager.initialize();
        if (!this.isConnected || this.generation !== generation) { manager.widget?.app?.unmount(); return; }
        if (!manager.widget) throw new Error('Review renderer did not mount');
        this.manager = manager;
        this.dataset.dioReviewLoaded = 'true';
        this.restoreRendering();
        this.observer?.disconnect();
      } catch (error) {
        this.pending = false;
        console.warn('Reviews will retry when requested', error);
      } finally {
        if (this.generation === generation) this.pending = false;
      }
    }
    restoreRendering() {
      // Containment is only for the server fallback. Native dialogs and
      // menus must retain Judge.me's normal overflow and layout behavior.
      this.style.removeProperty('min-height');
      this.style.removeProperty('content-visibility');
      this.style.removeProperty('contain-intrinsic-block-size');
    }
    async nativeFallback(base, data, key) {
      // If an app release changes the manager interface, use its declared
      // native entry point for all pending review blocks on this page.
      styles(data, key, base);
      document.querySelectorAll('[data-dio-review-entry-point]').forEach(root => {
        root.dataset.entryPoint = root.dataset.dioReviewEntryPoint;
      });
      const url = new URL(this.root.dataset.entryPoint, base).href;
      if (![...document.scripts].some(script => script.src === url)) {
        const script = document.createElement('script'); script.type = 'module'; script.src = url; document.head.append(script);
      }
      this.observer?.disconnect();
      this.dataset.dioReviewLoaded = 'native';
      this.restoreRendering();
    }
    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.parser?.disconnect(); this.observer?.disconnect();
      if (this.intent) document.removeEventListener('click', this.intent, true);
      if (this.hash) window.removeEventListener('hashchange', this.hash);
      this.manager?.widget?.app?.unmount();
      this.manager = null; this.pending = false; this.started = false;
      if (this.root) delete this.root.dataset.entryPoint;
    }
  }
  customElements.define('dio-review-lazy', ReviewLazy);
})();
